/* eslint-disable react/no-children-prop */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { Formik, Form } from 'formik';
import { useQuery } from '@apollo/client';
import * as Yup from 'yup';
import * as Styled from '../../CouponDialog.styled';
import { post } from '../../../../util/request';
import { trackEvent } from '../../../../util/track';
import { contacts_service_url } from '../../../../util/config';
import { getCookieDomain } from '../../../../util/util';
import { GET_USER_LOCALE } from '../../../App.gql';

const CouponSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Required')
});

const TEN_YEARS = 365 * 10;

/**
 * Coupon Dialog Form allows a user to sign up for Bombas' mailing list and receive a discount code.
 */
const CouponDialogForm = ({
  onComplete,
  onReject,
  isAutoApplied,
  discountPercentage
}) => {
  const { data: localeData } = useQuery(GET_USER_LOCALE);
  const [isFetching, setIsFetching] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    trackEvent({ event: 'Coupon Dialog Email View' });
  }, []);

  const handleSubmit = (values) => {
    setIsFetching(true);

    post(
      contacts_service_url,
      {
        contact: { email: values.email },
        created_source: 'coupon_modal'
      },
      { headers: { 'x-bom-country': localeData.user?.locale?.country } }
    )
      .then(() => {
        Cookies.set('optimizelyNewCustomerOfferEmailCaptureSuccess', true, {
          domain: getCookieDomain(),
          expires: TEN_YEARS
        });
        onComplete({ email: values.email, emailAlreadyExists: false });
      })
      .then(() => {
        trackEvent(
          { event: 'Mailing List Sign-Up', customer: { returning: false } },
          {
            event: 'croTest',
            expName: 'Optimizely - New Customer Offer Email Capture',
            expGroup: 'Sign-Up Success',
            expVariation: ''
          }
        );
      })
      .catch((error) => {
        setIsFetching(false);

        if (error.response) {
          Cookies.set('optimizelyNewCustomerOfferEmailCaptureSuccess', true, {
            domain: getCookieDomain(),
            expires: TEN_YEARS
          });

          // Ignore existing email messaging when auto application is enabled
          if (isAutoApplied) {
            onComplete({
              email: values.email,
              emailAlreadyExists: error.response.message === 409
            });
          } else if (error.response.statusCode === 409) {
            onComplete({ email: values.email, emailAlreadyExists: true });
          } else formRef.current.setErrors({ email: error.response.message });

          // Send tracking event only for server errors.
          trackEvent(
            {
              event: 'Mailing List Sign-Up Failure',
              customer: { returning: true }
            },
            {
              event: 'croTest',
              expName: 'Optimizely - New Customer Offer Email Capture',
              expGroup: 'Sign-Up Failure',
              expVariation: error.response.statusCode
            }
          );
        } else {
          formRef.current.setErrors({
            email: 'Unable to contact server. Please try again.'
          });
        }
      });
  };

  return (
    <Styled.ContentContainer>
      <Styled.Logo />
      <Styled.Heading>
        Get {discountPercentage}% Off
        {!isAutoApplied && (
          <>
            <br />
            Your First Order
          </>
        )}
      </Styled.Heading>
      <Styled.SubHeading>
        Enter your email for your code, plus the latest Bombas news.
      </Styled.SubHeading>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={CouponSchema}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        <Form>
          <Styled.TextInput
            id="couponFormEmail"
            name="email"
            type="email"
            label="Email address"
            autoComplete="email"
          />
          <Styled.ButtonContainer>
            <Styled.Button
              type="submit"
              size="large"
              variant="primary"
              isLoading={isFetching}
              children={`Get my ${discountPercentage}% off`}
            />
            <Styled.ButtonLink type="button" onClick={onReject}>
              Continue Shopping
            </Styled.ButtonLink>
          </Styled.ButtonContainer>
        </Form>
      </Formik>
      <Styled.Disclaimer>
        By providing my email, I am consenting to receive Bombas emails and
        Email-Based Advertising. For more information, please see our{' '}
        <a href="/pages/privacy-policy">Privacy Policy</a>.
      </Styled.Disclaimer>
    </Styled.ContentContainer>
  );
};

CouponDialogForm.propTypes = {
  /**
   * A function that is called upon successful mailing list signup.
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * A function that is called to close the form.
   */
  onReject: PropTypes.func.isRequired,
  /**
   * If `true`, coupon is being auto applied.
   */
  isAutoApplied: PropTypes.bool,
  /**
   * Percentage off value of the discount
   */
  discountPercentage: PropTypes.number
};

CouponDialogForm.defaultProps = {
  isAutoApplied: false,
  discountPercentage: 20
};

export default CouponDialogForm;
